// IMPORT REACT
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom';

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material'

import CustomBreadcrumbs from '../../../components/BreadcrumbsComponent/CustomBreadcrumbs';
import AddIcon from "@mui/icons-material/Add";
import { EncryptDecryptService } from '../../../services/common/EncryptionService';
import SkeletonWrapper from '../../../components/Skeleton/SkeletonWrapper';
import { CustomTextField } from '../../../components/CustomFields/TextField';
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { PAGE_SIZE } from '../../../services/common/const';
import parse from "html-react-parser";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { AlertType } from '../../../services/models/AlertType';
import { PreferencesContext } from '../../../PreferenceContext';
import {
  DeleteDiagnosisTemplate,
  GetDiagnosisTemplateList
} from '../../../services/DiagnosisTemplateService';

// SCHEMA
interface Data {
  diagnosisTemplateId: number;
  code: string;
  authorizePersion: string;
  organization: string;
  designation: string;
  email: string;
  website: string;
  plan: string;
  none: string;
}

// SCHEMA FOR RESULTDATA
interface ResultData {
  diagnosisTemplateColl: Array<any>;
  tabTypeColl: Array<any>;
  totalCount: number;
  totalFinalGrandAmount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  diagnosisTemplateColl: [],
  tabTypeColl: [],
  totalCount: 0,
  totalFinalGrandAmount: 0,
};

type Order = "asc" | "desc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  diagnosisTemplateId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  diagnosisTemplateId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
};

// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

// ADD CENTRALIZED ERROR HANDLING UTILITY
const handleError = (error: any, context: any, updateContext: any) => {
  updateContext({
    ...context,
    isAlert: true,
    alertMessage: typeof error === "string" ? error : error?.message,
    alertType: AlertType.Error,
  });
};

// ADD API CONFIGURATION AND REQUEST CANCELLATION
const createAbortController = () => new AbortController();

const createBaseParams = (userContext: any) => {
  return new URLSearchParams({
    consumerId: userContext.consumerId?.toString() || "",
    organizationId: userContext.organizationId?.toString() || "",
    orgId: userContext.organizationId?.toString() || "",
    branchId: userContext.branchId?.toString() || "",
  });
};

// ADD DEBOUNCE UTILITY
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export default function DiagnosisTemplateList() {
  // HOOKS AND CONTEXT
  const navigate = useNavigate()
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const isSuperAdmin =
    (userContext?.loginDetail?.userRole || "").toString().toLowerCase() ===
    "superadmin";
  // STATES 
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [value, setValue] = useState("");
  const [order, setOrder] = React.useState<Order>(""); // DON"T USE  DIFFERENT STATE FOR EVERY PARAMETER USE PARAMETER STATE INSTAND
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGE_SIZE);
  const [page, setPage] = React.useState(0);
  const [anchorEl, setAnchorEl3] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [diagnosisTemplateId, setDiagnosisTemplateId] = useState(0);
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);
  const [counData, setCounData] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [page_Index, setPage_Index] = useState(1);
  const [openDeleteDilog, setOpenDeleteDilog] = React.useState(false);

  const [param, setParam] = useState({
    ...parameters,
  });

  // GETTING SEACH PARAMMS FOR PAGINATION
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let ePage = urlParams.get("page");
  let encodedQuery = urlParams.get("q");

  let query = "";
  let curPage = 0;

  if (
    ePage != null &&
    ePage !== "" &&
    ePage !== undefined &&
    encodedQuery !== null &&
    encodedQuery !== undefined
  ) {
    curPage = +ePage;
    // ENCODE QUERY
    encodedQuery = encodeURIComponent(encodedQuery);

    // DECODE QUERY
    query = decodeURIComponent(encodedQuery);
    // SET PAYMENTMODE FROM URL
  } else {
    navigate("/not-found");
  }

  // HANDLE POPUP FOR EDIT AND DELETE
  const handleOpenMore = (
    event: React.MouseEvent<HTMLButtonElement>,
    diagnosisTemplateId: any
  ) => {
    setAnchorEl3(event.currentTarget);
    setDiagnosisTemplateId(diagnosisTemplateId);
  };

  // CLOSE POPUP
  const handleCloseMore = () => {
    setAnchorEl3(null);
  };

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              fontWeight: "bold",
            }}
            align="left">
            Template Name
          </TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
    );
  }

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number]
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const [abortController, setAbortController] = useState(
    createAbortController()
  );

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    if (curPage !== value) {
      navigate(
        `?page=${value}&q=${encodedQuery}`
      );
    }
  };

  // HANDLE UPDATE
  const handleUpdate = (diagnosisTemplateId: any) => {
    let encryptDiagnosisTemplatetId: any = edService.encrypt(diagnosisTemplateId);
    navigate(
      {
        pathname: "diagnosis-template-save",
        search: `?q=${encodeURIComponent(encryptDiagnosisTemplatetId)}`,
      },
      {
        state: {
          title: "Edit",
          button: "Update",
          diagnosisTemplateId: diagnosisTemplateId,
        },
      }
    );
  };

  // HANDLE DELETE WARNING POPUP
  const handleDeleteDilog = (value: any) => {
    setOpenDeleteDilog(value);
  };

  // UPDATE THE SEARCHVALUE FUNCTION TO MAINTAIN CURRENT DATE RANGE
  const searchValue = (val: any) => {
    val = val.trim();
    val = encodeURIComponent(val);
    navigate(`?page=1&q=${val}`);
  };

  // UPDATE THE DEBOUNCEDSEARCH TO USE THE CURRENT DATE RANGE
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      searchValue(searchTerm);
    }, 500),
    []
  );

  const getDiagnosisTemplateList = async () => {
    try {
      abortController.abort();
      const newAbortController = createAbortController();
      setAbortController(newAbortController);

      const baseParams = createBaseParams(userContext);
      // ADD PAGINATION, SEARCH, AND DATE PARAMS
      baseParams.append(
        "departmentId",
        isSuperAdmin ? "0" : userContext?.departmentId?.toString() || "0",
      );
      baseParams.append(
        "specialityId",
        isSuperAdmin ? "0" : userContext?.specialityId?.toString() || "0",
      );
      baseParams.append("pageIndex", param.pageIndex?.toString());
      baseParams.append("pageSize", param.pageSize?.toString());
      baseParams.append("sortColumn", param.sortColumn);
      baseParams.append("sortDirection", param.sortDirection);
      baseParams.append("searchValue", param.searchValue || "");
      baseParams.append("mode", "LIST");

      const result: any = await GetDiagnosisTemplateList(baseParams);

      if (result?.data?.data?.diagnosisTemplateColl) {
        const resultData: ResultData = result?.data?.data;
        let diagnosisTemplateColl = resultData?.diagnosisTemplateColl || [];

        let dataCount = diagnosisTemplateColl.length;
        let showDataCount = Math.ceil(dataCount / param.pageSize);

        setResultData({
          ...resultData,
          diagnosisTemplateColl: diagnosisTemplateColl,
          totalCount: dataCount, // TOTAL AFTER FILTER
        });

        setCounData(showDataCount);
        setPageStartIndex((page_Index - 1) * param.pageSize + 1);
        setPageEndIndex(
          (page_Index - 1) * param.pageSize + diagnosisTemplateColl?.length
        );
        setTotalCount(dataCount);
      }

      setIsLoading(false);
    } catch (e: any) {
      if (e.name !== "AbortError") {
        setIsLoading(false);
        handleError(e, userContext, updateUserContext);
      }
    }
  };

  // UPDATE THE CLEAR SEARCH FUNCTIONALITY
  const handleClearSearch = () => {
    setValue(""); // CLEAR THE INPUT VALUE
    searchValue(""); // CLEAR THE SEARCH PARAMETER
  };

  // HANDLE DELETE
  const deleteRow = async (diagnosisTemplateIds: any) => {
    try {
      setAnchorEl3(null);
      handleDeleteDilog(false);
      let DeleteData = {
        diagnosisTemplateIds: diagnosisTemplateIds?.toString(),
        branchId: userContext?.branchId?.toString(),
        organizationId: userContext?.organizationId?.toString(),
        consumerId: userContext?.consumerId?.toString(),
        isDeleted: true,
        dataManagedBy: userContext?.loginDetail?.userId,
      };
      const result: any = await DeleteDiagnosisTemplate(DeleteData);
      if (result) {
        await getDiagnosisTemplateList();
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Success,
        });
      }
    } catch (e) {
      handleError(e, userContext, updateUserContext);
    }
  }

  // HANDLE CHECK ROW
  const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = resultData?.diagnosisTemplateColl?.map(
        (n: any) => n?.diagnosisTemplateId || 0
      );
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await getDiagnosisTemplateList();
    } catch (e: any) {
      if (e.name !== "AbortError") {
        handleError(e, userContext, updateUserContext);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setParam({
      ...param,
      searchValue: query,
      pageIndex: curPage,
    });
  }, [query, curPage]);

  useEffect(() => {
    fetchData();
  }, [param]);

  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <>
      <Box>
        <Box className="main-container">
          <Grid container className="page-header">
            <Grid item xs={6}>
              <Box sx={{ display: "flex", height: "100%", alignItems: "end" }}>
                <CustomBreadcrumbs />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    handleUpdate(0);
                  }}
                  size="medium"
                  startIcon={<AddIcon />}>
                  Add
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Grid container className="list-container">
            <Grid item xs={12}>
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={12} sm={6} md={3} xl={2}>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-search-box">
                    <CustomTextField
                      id="searchBox"
                      placeholder="Search..."
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          debouncedSearch(value);
                        }
                      }}
                      type="text"
                      variant="outlined"
                      size="small"
                      className="width-100-percent"
                      onChange={(e) => setValue(e.target.value)}
                      value={value}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <IconButton
                              size="small"
                              onClick={() => debouncedSearch(value)}>
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                        endAdornment: value && (
                          <IconButton
                            aria-label="clear search"
                            onClick={handleClearSearch}>
                            <ClearIcon />
                          </IconButton>
                        ),
                      }}
                    />
                  </SkeletonWrapper>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} mt={2}>
              <TableContainer>
                <Table>
                  <EnhancedTableHead
                    numSelected={resultData?.diagnosisTemplateColl?.length || 0}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleCheckSelectAll}
                    rowCount={resultData?.diagnosisTemplateColl?.length}
                  />
                  <TableBody>
                    {isLoading ? (
                      Array(skeletonRowCount)
                        ?.fill(0)
                        ?.map((skeletonRow, index) => {
                          return (
                            <TableRow key={"skeleton-row" + index?.toString()}>
                              <TableCell colSpan={8}>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input">
                                  <span></span>
                                </SkeletonWrapper>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    ) : (
                      <>
                        {stableSort(
                          resultData?.diagnosisTemplateColl,
                          getComparator(order, orderBy)
                        )
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((item: any, index: any) => {
                            return (
                              <TableRow
                                key={`diagnosisTemplate-row-${item?.diagnosisTemplateId || index}`}>
                                <TableCell>
                                  <Link
                                    key={`link-${item?.diagnosisTemplateId}`}
                                    className="text-link"
                                    to={{
                                      pathname: "diagnosis-template-save",
                                      search: `?q=${encodeURIComponent(
                                        edService.encrypt(
                                          item?.diagnosisTemplateId || 0
                                        ) || 0
                                      )}`,
                                    }}>
                                    {item?.title}
                                  </Link>
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="More" placement="top">
                                    <IconButton
                                      key={`more-${item?.diagnosisTemplateId}`}
                                      onClick={(e: any) => {
                                        handleOpenMore(e, item?.diagnosisTemplateId || 0);
                                      }}>
                                      <MoreVertRoundedIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        {resultData?.diagnosisTemplateColl?.length <= 0 && (
                          <TableRow>
                            <TableCell align="center" colSpan={8}>
                              No record(s) found
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} paddingTop={1}>
              {resultData?.diagnosisTemplateColl?.length > 0 && (
                <Box className="pagination">
                  <Box className="pagination-label">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-label">
                      {pageStartIndex} - {pageEndIndex} of {totalCount}
                    </SkeletonWrapper>
                  </Box>
                  <Box className="pagination-stack">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-stack">
                      <Stack component="div">
                        <Pagination
                          color="primary"
                          count={counData} // NUMBER OF PAGES
                          page={curPage} // CURRENT PAGE
                          onChange={handleChangePageNew} //HANDLE PAGE CHANGE
                        />
                      </Stack>
                    </SkeletonWrapper>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Popover
        elevation={1}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMore}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                handleUpdate(diagnosisTemplateId);
              }}>
              <ListItemText primary="Edit" />
            </ListItemButton>
          </ListItem>
          {userContext.loginDetail.userRole !== "user" && (
            <ListItem disablePadding>
              <ListItemButton
                className="delete-container"
                onClick={() => {
                  handleDeleteDilog(true);
                }}>
                Delete
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Popover>

      <Dialog
        open={openDeleteDilog}
        onClose={() => {
          handleDeleteDilog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this diagnosis template?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              deleteRow(diagnosisTemplateId);
            }}
            className="button-delete">
            Yes
          </Button>
          <Button
            onClick={(e) => {
              handleDeleteDilog(false);
            }}
            autoFocus>
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
